import {
  applyAdvanceRace,
  applyAdvanceRound,
  applyConfirmRecap,
  applyConfirmRoundRecap,
  applyDrawCoins,
  applyForfeitTurn,
  applyPlaceCoins,
  applyResolveRound,
  applySelectCharacters,
  applyShufflePool,
  type HorrorRaceState,
  type ValidationResult,
  validateAdvanceRound,
  validateConfirmRecap,
  validateConfirmRoundRecap,
  validateDrawAndPlace,
  validateForfeitTurn,
  validateResolveRound,
  validateSelectCharacters,
} from "@cloudnest/horror-race-engine";
import {
  type HorrorRaceReplayEvent,
  toHorrorRaceReplayEvent,
} from "./event-adapter";
import type { HorrorRaceReplayFixtureEvent } from "./replay-fixtures.generated";

export interface HorrorRaceReplaySnapshot {
  readonly index: number;
  readonly event: HorrorRaceReplayFixtureEvent;
  readonly replayEvent: HorrorRaceReplayEvent;
  readonly state: HorrorRaceState;
  readonly phase: HorrorRaceState["phase"];
  readonly validationNote: string;
}

function requireState(
  state: HorrorRaceState | null,
  eventType: HorrorRaceReplayEvent["eventType"],
): HorrorRaceState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function assertValid(
  condition: boolean,
  event: HorrorRaceReplayFixtureEvent,
  message: string,
): void {
  if (!condition) {
    throw new Error(
      `Invalid Horror Race replay event #${event.sequenceNumber} ${event.eventType}: ${message}`,
    );
  }
}

function requireValid(
  result: ValidationResult,
  event: HorrorRaceReplayFixtureEvent,
): void {
  assertValid(result.valid, event, result.error ?? "Validation failed");
}

function assertHostActor(
  state: HorrorRaceState,
  event: HorrorRaceReplayFixtureEvent,
): void {
  assertValid(
    event.userId === state.hostPlayerId,
    event,
    "Actor must be the host",
  );
}

function validateRequestDraw(
  state: HorrorRaceState,
  event: HorrorRaceReplayFixtureEvent,
  replayEvent: Extract<HorrorRaceReplayEvent, { eventType: "request_draw" }>,
): void {
  assertValid(
    state.phase === "ROUND_DRAW_AND_PLACE",
    event,
    "Not in draw and place phase",
  );
  assertValid(
    state.drawnCoins.length === 0,
    event,
    "Coins already drawn this turn",
  );

  const currentPlayerIndex = state.turnOrder[state.currentTurnIndex];
  const currentPlayer = state.players[currentPlayerIndex];
  assertValid(currentPlayer !== undefined, event, "Current player not found");
  assertValid(event.userId === currentPlayer.id, event, "Not your turn");

  if (replayEvent.payload?.playerId !== undefined) {
    assertValid(
      replayEvent.payload.playerId === event.userId,
      event,
      "Payload playerId does not match actor",
    );
  }
}

function validateShufflePool(
  state: HorrorRaceState,
  event: HorrorRaceReplayFixtureEvent,
  replayEvent: Extract<HorrorRaceReplayEvent, { eventType: "shuffle_pool" }>,
): void {
  assertValid(
    state.phase === "ROUND_DRAW_AND_PLACE",
    event,
    "Not in draw and place phase",
  );
  assertValid(
    Number.isFinite(replayEvent.payload.seed),
    event,
    "shuffle_pool seed must be a finite number",
  );
}

function validateDrawCoins(
  state: HorrorRaceState,
  event: HorrorRaceReplayFixtureEvent,
): void {
  assertValid(
    state.phase === "ROUND_DRAW_AND_PLACE",
    event,
    "Not in draw and place phase",
  );
  assertValid(
    state.drawnCoins.length === 0,
    event,
    "Coins already drawn this turn",
  );
  assertValid(
    state.coinPool.length > 0,
    event,
    "Cannot draw from an empty coin pool",
  );
}

function validateScoreRace(
  state: HorrorRaceState,
  event: HorrorRaceReplayFixtureEvent,
): void {
  assertValid(
    state.phase === "RACE_SCORING",
    event,
    "Not in race scoring phase",
  );
  assertValid(
    state.players.every((player) => player.hasConfirmedRecap),
    event,
    "Not all players have confirmed the race recap",
  );
}

function validateReplayEvent(
  state: HorrorRaceState | null,
  event: HorrorRaceReplayFixtureEvent,
  replayEvent: HorrorRaceReplayEvent,
): void {
  assertValid(event.userId.trim() !== "", event, "Missing actor");

  switch (replayEvent.eventType) {
    case "init":
      assertValid(state === null, event, "init must be the first replay event");
      assertValid(
        replayEvent.payload.state.phase === "SETUP_CHARACTER_SELECTION",
        event,
        "init state must start in character selection",
      );
      assertHostActor(replayEvent.payload.state, event);
      return;
    case "select_characters": {
      const current = requireState(state, replayEvent.eventType);
      assertValid(
        event.userId === replayEvent.payload.playerId,
        event,
        "Actor must match selected player",
      );
      requireValid(
        validateSelectCharacters(current, {
          type: "SELECT_CHARACTERS",
          ...replayEvent.payload,
        }),
        event,
      );
      return;
    }
    case "request_draw":
      validateRequestDraw(
        requireState(state, replayEvent.eventType),
        event,
        replayEvent,
      );
      return;
    case "shuffle_pool":
      assertHostActor(requireState(state, replayEvent.eventType), event);
      validateShufflePool(
        requireState(state, replayEvent.eventType),
        event,
        replayEvent,
      );
      return;
    case "draw_coins":
      assertHostActor(requireState(state, replayEvent.eventType), event);
      validateDrawCoins(requireState(state, replayEvent.eventType), event);
      return;
    case "place_coins": {
      const current = requireState(state, replayEvent.eventType);
      assertValid(
        event.userId === replayEvent.payload.playerId,
        event,
        "Actor must match placing player",
      );
      requireValid(
        validateDrawAndPlace(current, {
          type: "DRAW_AND_PLACE",
          ...replayEvent.payload,
        }),
        event,
      );
      return;
    }
    case "forfeit_turn":
      requireValid(
        validateForfeitTurn(requireState(state, replayEvent.eventType), {
          type: "FORFEIT_TURN",
          playerId: event.userId,
        }),
        event,
      );
      return;
    case "resolve_round": {
      const current = requireState(state, replayEvent.eventType);
      assertHostActor(current, event);
      requireValid(
        validateResolveRound(current, {
          type: "RESOLVE_ROUND",
          resolverId: current.hostPlayerId,
        }),
        event,
      );
      return;
    }
    case "confirm_round_recap":
      assertValid(
        event.userId === replayEvent.payload.playerId,
        event,
        "Actor must match round recap confirming player",
      );
      requireValid(
        validateConfirmRoundRecap(requireState(state, replayEvent.eventType), {
          type: "CONFIRM_ROUND_RECAP",
          ...replayEvent.payload,
        }),
        event,
      );
      return;
    case "advance_round": {
      const current = requireState(state, replayEvent.eventType);
      assertHostActor(current, event);
      requireValid(
        validateAdvanceRound(current, {
          type: "ADVANCE_ROUND",
          resolverId: current.hostPlayerId,
        }),
        event,
      );
      return;
    }
    case "confirm_recap":
      assertValid(
        event.userId === replayEvent.payload.playerId,
        event,
        "Actor must match race recap confirming player",
      );
      requireValid(
        validateConfirmRecap(requireState(state, replayEvent.eventType), {
          type: "CONFIRM_RECAP",
          ...replayEvent.payload,
        }),
        event,
      );
      return;
    case "score_race":
      assertHostActor(requireState(state, replayEvent.eventType), event);
      validateScoreRace(requireState(state, replayEvent.eventType), event);
      return;
  }
}

function applyReplayEvent(
  state: HorrorRaceState | null,
  event: HorrorRaceReplayEvent,
): HorrorRaceState {
  switch (event.eventType) {
    case "init":
      return event.payload.state;
    case "select_characters":
      return applySelectCharacters(requireState(state, event.eventType), {
        type: "SELECT_CHARACTERS",
        ...event.payload,
      });
    case "request_draw":
      return requireState(state, event.eventType);
    case "shuffle_pool":
      return applyShufflePool(
        requireState(state, event.eventType),
        event.payload.seed,
      );
    case "draw_coins":
      return applyDrawCoins(requireState(state, event.eventType));
    case "place_coins":
      return applyPlaceCoins(requireState(state, event.eventType), {
        type: "DRAW_AND_PLACE",
        ...event.payload,
      });
    case "forfeit_turn":
      return applyForfeitTurn(requireState(state, event.eventType));
    case "resolve_round":
      return applyResolveRound(requireState(state, event.eventType));
    case "confirm_round_recap":
      return applyConfirmRoundRecap(requireState(state, event.eventType), {
        type: "CONFIRM_ROUND_RECAP",
        ...event.payload,
      });
    case "advance_round":
      return applyAdvanceRound(requireState(state, event.eventType));
    case "confirm_recap":
      return applyConfirmRecap(requireState(state, event.eventType), {
        type: "CONFIRM_RECAP",
        ...event.payload,
      });
    case "score_race":
      return applyAdvanceRace(requireState(state, event.eventType));
  }
}

export function buildHorrorRaceReplaySnapshots(
  events: readonly HorrorRaceReplayFixtureEvent[],
): readonly HorrorRaceReplaySnapshot[] {
  const snapshots: HorrorRaceReplaySnapshot[] = [];
  let state: HorrorRaceState | null = null;

  for (const event of events) {
    assertValid(
      event.sequenceNumber === snapshots.length + 1,
      event,
      `Expected sequenceNumber ${snapshots.length + 1}`,
    );
    const replayEvent = toHorrorRaceReplayEvent(event);
    validateReplayEvent(state, event, replayEvent);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      phase: state.phase,
      validationNote: "validated",
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Horror Race event log");
  }

  return snapshots;
}
